import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ComponentRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
    BadgeDetailDialogComponent,
    BadgeDialogComponent,
    LoadMoreButtonComponent,
    SelectComponent,
    SubpageHeaderComponent,
    TrackByItem,
} from '@components';
import { BadgeSource, HomeSectionLabel, ProductUid } from '@constants';
import { environment } from '@environments/environment';
import { BreakpointService, KeplerCredlyService, Product, ProductService, RouteService } from '@services';
import { Badge } from '@types';
import { BehaviorSubject, firstValueFrom, map, Observable, Subscription, switchMap, tap } from 'rxjs';
import { GknRoutePath } from 'src/app/app-routing.enum';

@Component({
    selector: 'app-home-badges',
    imports: [
        CommonModule,
        LoadMoreButtonComponent,
        MatTooltipModule,
        SubpageHeaderComponent,
        MatIconModule,
        SelectComponent,
        RouterModule,
    ],
    templateUrl: './home-badges.component.html',
    styleUrls: ['./home-badges.component.scss'],
})
export class HomeBadgesComponent implements OnInit, TrackByItem<Badge> {
    private cdr = inject(ChangeDetectorRef);
    private credly = inject(KeplerCredlyService);
    private breakpointService = inject(BreakpointService);
    private dialog = inject(MatDialog);
    private productService = inject(ProductService);
    private routeService = inject(RouteService);

    /** h4 text for this section */
    title = HomeSectionLabel.BADGES;
    @Input() showSectionCtaLabel;
    @Input() sectionCtaUrl;
    @Input() openNonModal = false;
    @Input() showBadgeTitle = false;
    @Input() modifier = 6;
    @Input() length = 6;
    private subs: Subscription[] = [];
    @Input() isEmployeeView = false;
    @Input() isAboutMeView = false;
    @Input() showFilter = false;
    BadgeSource = BadgeSource;
    filterControl = new FormControl<BadgeSource>(BadgeSource.ALL);
    private selectedFilter$ = new BehaviorSubject<BadgeSource>(BadgeSource.ALL);

    /** ref to the subsection header */
    @ViewChild('badgesHeaderRef') headerRef: ComponentRef<SubpageHeaderComponent>;
    /** list of user's badges from credly.com */
    badges$: Observable<Badge[]>;
    /** max number of items to show  */
    max: number;
    /** whether or not the load more button is disabled */
    isLoadMoreDisabled = false;
    currentProduct: Product;
    signUpUrl: string;
    position: any;
    isProfilePage$: Observable<boolean>;
    isAboutMePage$: Observable<boolean>;
    selectedProduct$: Observable<Product>;

    ngOnInit(): void {
        this.badges$ = this.selectedFilter$.pipe(
            switchMap((filter) =>
                this.credly.badges$.pipe(
                    map((badges) => (filter === BadgeSource.ALL ? badges : badges.filter((b) => b.source === filter))),
                ),
            ),
            tap((badges) => {
                if (badges.length < this.modifier) this.isLoadMoreDisabled = true;
                this.max = badges.length;
            }),
        );

        this.subs.push(
            this.filterControl.valueChanges.subscribe((value) => {
                const filter: BadgeSource = Array.isArray(value) ? value[0] : value;
                this.length = this.modifier;
                this.isLoadMoreDisabled = false;
                this.selectedFilter$.next(filter ?? BadgeSource.ALL);
            }),
        );

        this.subs.push(
            this.productService.selectedProduct$.subscribe((product: Product) => {
                if (product) {
                    this.currentProduct = product;
                    this.setSignUpUrl(this.currentProduct.uid);
                    this.cdr.detectChanges();
                }
            }),
        );

        this.selectedProduct$ = this.productService.selectedProduct$;

        this.isProfilePage$ = this.routeService.currentRoute$.pipe(
            map((currentRoute) => currentRoute === GknRoutePath.PROFILE || currentRoute === GknRoutePath.HOME),
        );

        this.isAboutMePage$ = this.routeService.currentRoute$.pipe(
            map((currentRoute) => currentRoute === GknRoutePath.PROFILE),
        );
    }

    refreshLength(val: number): void {
        this.length = val;
        this.cdr.detectChanges();
    }

    trackByItem(index: number, item: Badge): NonNullable<number | string> {
        return item.id || index;
    }

    /**
     * Open dialog with all badges
     * @param badges - Array of badges to display
     */
    openBadgeDialog(badges: Badge[]) {
        firstValueFrom(this.breakpointService.mobile$).then((isMobile) => {
            this.dialog.open(BadgeDialogComponent, {
                width: isMobile ? '' : '1246px',
                maxWidth: BreakpointService.contentMaxWidth,
                autoFocus: false,
                data: {
                    badges: badges,
                    title: 'Badges',
                    dataAnalytics: ['badges', 'dialog'],
                },
            });
        });
    }

    /**
     * Open dialog with all badges
     * @param badges - Array of badges to display
     */
    openBadgeDetailDialog(badge: Badge, event?: MouseEvent) {
        firstValueFrom(this.breakpointService.mobile$).then(() => {
            const target = event?.target as HTMLElement;
            const rect = target?.getBoundingClientRect();

            if (this.isEmployeeView && rect) {
                // Special positioning for employee view (third column)
                this.position = {
                    top: `${rect.top + 25}px`,
                    left: `${rect.left - rect.width * 8.5}px`, // Shift dialog to the left
                };
            } else if (this.isAboutMeView && rect) {
                this.position = {
                    top: `${rect.top + 25}px`,
                    left: `${Math.min(window.innerWidth - rect.width / 2, rect.left - rect.width / 2)}px`,
                };
            } else if (rect) {
                // Default positioning
                this.position = {
                    top: `${rect.top + 25}px`,
                    left: `${rect.left + rect.width / 2}px`,
                };
            }

            this.dialog.open(BadgeDetailDialogComponent, {
                maxWidth: BreakpointService.contentMaxWidth,
                autoFocus: false,
                hasBackdrop: true, // Enable backdrop for click detection
                backdropClass: 'transparent-backdrop', // Use a transparent backdrop
                panelClass: 'no-backdrop-dialog',
                position: rect // Position configuration for the dialog to appear near the selected badge
                    ? this.position
                    : undefined,
                data: {
                    badge: badge,
                },
            });
        });
    }

    /**
     * Sets Sign-up Url based on Product UId
     * @param productUid - Unique Product Id
     */
    private setSignUpUrl(productUid: ProductUid): void {
        switch (productUid) {
            case ProductUid.GENESYS_CLOUD_CX:
                this.signUpUrl = `${environment.education.url}/certification/cloud`;
                break;
            case ProductUid.GENESYS_ENGAGE_ON_PREM:
                this.signUpUrl = `${environment.education.url}/certification/genesys-engage-exams`;
                break;
            default:
                this.signUpUrl = '';
                break;
        }
    }
}
